/** Payment is reported from the authenticated checkout read, never the callback URL. */
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useStorefront } from "../app/StorefrontContext.js";
import { useAnnounce } from "../components/Announcer.js";
import { InternalLink } from "../components/InternalLink.js";
import { CheckoutCopy, PurchaseProgress } from "../components/PurchaseSummary.js";
import { Money, StatusChip } from "../components/primitives.js";
import { ErrorBlock, LoadingBlock, SuccessBlock } from "../components/states.js";
import { commandID, continueCheckout } from "../lib/commands.js";
import { checkoutStage, holdNotice } from "../lib/labels.js";
import type { CheckoutState } from "../lib/publicContract.js";
import { orderHelpHref } from "../lib/routes.js";
import { useCheckoutStatus } from "../lib/useCheckoutStatus.js";

export function ConfirmationView({ orderID }: { orderID: string }): ReactNode {
  const { transport, csrfToken, handoff, tabReady } = useStorefront();
  const [continuationProblem, setContinuationProblem] = useState<string | null>(null);
  const asked = useRef(false);
  const commandRef = useRef(commandID());
  const matchingHandoff = handoff.orderID === null || handoff.orderID === orderID;
  const checkoutToken = matchingHandoff ? handoff.checkoutToken ?? undefined : undefined;
  const quoteID = matchingHandoff ? handoff.quoteID : null;
  const canRead = checkoutToken !== undefined || csrfToken !== null;

  useEffect(() => {
    // Wait for hydration to adopt the tab's capability. Manual refresh only reads;
    // it never repeats this continuation or creates/captures another payment.
    if (!tabReady || !canRead || asked.current) return;
    asked.current = true;
    void continueCheckout(transport, orderID, commandRef.current, {
      ...(csrfToken === null ? {} : { csrfToken }),
      ...(checkoutToken === undefined ? {} : { checkoutToken })
    }).then((outcome) => {
      if (outcome.kind !== "ok") setContinuationProblem(
        outcome.kind === "offline"
          ? "The connection dropped before the server answered. The payment result is unknown here."
          : "The request to retrieve the payment result was not accepted. This does not establish whether payment completed."
      );
    });
  }, [tabReady, canRead, transport, orderID, csrfToken, checkoutToken]);

  const status = useCheckoutStatus(transport, {
    quoteID,
    ...(checkoutToken === undefined ? {} : { checkoutToken }),
    ...(csrfToken === null ? {} : { csrfToken }),
    enabled: tabReady && canRead && quoteID !== null
  });
  const hasResult = status.phase.kind === "ready" || status.phase.kind === "exhausted";

  return <div className="stack confirmation">
    <h1 className="t-display-l">Your order</h1>
    <p>Order reference <span className="t-id">{orderID}</span></p>
    {!tabReady ? <LoadingBlock cards={1} waitingFor="this tab's order reference" /> : !canRead || quoteID === null ? (
      <div className="state state--accent" role="status">
        <h2 className="t-display-xs">This browser has no way to look up that order.</h2>
        <p className="state-detail">This tab does not have access to the checkout that started this order. We cannot confirm the payment result here. Do not pay again.</p>
        <OrderHelp orderID={orderID} />
      </div>
    ) : <>
      {continuationProblem !== null && !hasResult ? <div className="state state--accent" role="status">
        <p className="state-title">We could not ask for the result from this page.</p>
        <p>{continuationProblem}</p>
        <p>Do not pay again. We are checking the saved order status.</p>
      </div> : null}
      <AuthoritativeResult status={status} orderID={orderID} />
    </>}
  </div>;
}

function OrderHelp({ orderID }: { orderID: string }): ReactNode {
  return <InternalLink className="btn btn--secondary" href={orderHelpHref(orderID)}>Ask about this order</InternalLink>;
}

function AuthoritativeResult({ status, orderID }: { status: ReturnType<typeof useCheckoutStatus>; orderID: string }): ReactNode {
  const announce = useAnnounce();
  const { phase } = status;
  useEffect(() => {
    if (phase.kind === "ready" || phase.kind === "exhausted") {
      if (phase.state.order_id === orderID) announce(checkoutStage(phase.state.status).title);
    }
  }, [phase, orderID, announce]);

  if (phase.kind === "idle" || phase.kind === "loading") {
    return <LoadingBlock cards={1} waitingFor="the order status from the server" />;
  }
  if (phase.kind === "failed") {
    return <ErrorBlock title="The state of this order could not be read."
      detail="We cannot confirm the payment result here. Do not pay again. Check status to read the latest order record."
      onRetry={status.refresh} retryLabel="Check status"><OrderHelp orderID={orderID} /></ErrorBlock>;
  }
  const state = phase.state;
  if ((state.order_id !== undefined || state.status === "PAID") && state.order_id !== orderID) {
    return <ErrorBlock title="This checkout belongs to a different order."
      detail="We cannot confirm this order from the saved checkout. Do not pay again."><OrderHelp orderID={orderID} /></ErrorBlock>;
  }
  const stage = checkoutStage(state.status);
  const onHold = state.fulfillment_status === "ON_HOLD" && state.hold_reason !== undefined;
  const ended = state.status === "EXPIRED" || state.status === "CANCELLED";
  return <div className="stack">
    <PurchaseProgress current={stage.paid ? "Confirmation" : ended ? null : state.status === "TAX_PENDING" ? "Review total" : "PayPal"} />
    {stage.paid ? <SuccessBlock title="Payment confirmed by PayPal"
      detail="PayPal confirmed payment and the server recorded it against this order. This page shows that order record.">
      <p>Paid <Money minor={state.total_minor} /></p>
    </SuccessBlock> : ended ? <ErrorBlock title={stage.title}
      detail={stage.detail + " If you already approved payment, do not pay again. Ask for help to confirm its outcome."} /> : onHold ? null : (
      <div className="state state--accent" data-testid="awaiting-authority" role="status">
        <p className="state-title t-body-l"><StatusChip label="Confirming" tone="accent" />{stage.title}</p>
        <p className="state-detail">{stage.detail}</p>
      </div>
    )}
    {onHold ? <HoldNoticeBlock state={state} /> : stage.paid ? (
      <p data-testid="fulfilment-unclaimed">This page reports payment only. Packing and shipping status are not available in this record.</p>
    ) : null}
    {phase.kind === "exhausted" ? <p role="status">Automatic status checking paused. Check status for the latest result. Do not pay again.</p> : null}
    <OrderLines state={state} />
    <div className="state-actions">
      <button className="btn btn--secondary" type="button" onClick={status.refresh}>Check status</button>
      <OrderHelp orderID={orderID} />
    </div>
  </div>;
}

function HoldNoticeBlock({ state }: { state: CheckoutState }): ReactNode {
  const paid = checkoutStage(state.status).paid;
  const notice = holdNotice(state.hold_reason ?? "HIGH_VALUE_REVIEW", paid);
  return <div className="state state--accent" data-testid="order-hold" role="status">
    <p className="state-title t-body-l"><StatusChip label="On hold" tone="accent" />{notice.title}</p>
    <p className="state-detail">{notice.detail}</p>
    <p className="state-detail">{notice.recovery}</p>
    {paid ? null : <p>Payment has not been confirmed in this order record. Use order help to resolve the hold before attempting another payment.</p>}
  </div>;
}

function OrderLines({ state }: { state: CheckoutState }): ReactNode {
  if (state.lines.length === 0) return null;
  const taxPending = state.status === "TAX_PENDING";
  return <section aria-labelledby="order-lines-title" className="summary">
    <h2 className="t-display-xs" id="order-lines-title">What is on this order</h2>
    <ul className="summary-lines">{state.lines.map((line) => <li key={line.physical_item_id}>
      <CheckoutCopy line={line} />
      <p>{taxPending ? "Line amount before final tax: " : "Line total: "}<Money minor={line.final_line_total_minor} size="s" /></p>
    </li>)}</ul>
    {taxPending ? <p>Amount before final tax <Money minor={state.total_minor} size="s" />. Tax is being calculated.</p> : (
      <dl className="summary-totals">
        <div><dt>Tax</dt><dd><Money minor={state.tax_minor} size="s" /></dd></div>
        <div><dt>Shipping</dt><dd><Money minor={state.shipping_minor} size="s" /></dd></div>
        <div><dt>Insurance</dt><dd><Money minor={state.insurance_minor} size="s" /></dd></div>
        <div className="summary-total"><dt>Total</dt><dd><Money minor={state.total_minor} size="s" /></dd></div>
      </dl>
    )}
  </section>;
}
